// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * How big is this bundle, in the units the context window is measured in?
 *
 * THE NUMBER IS AN ESTIMATE AND THE INTERFACE SAYS SO. Exact counting means
 * shipping a real tokenizer, and that costs roughly 800 KB brotli by every route
 * measured — `gpt-tokenizer` 3.4.0 is 53 MB unpacked, and in v3 its
 * `encoding/o200k_base` subpath is byte-identical to the bare import, so the
 * familiar "just use the subpath" advice buys nothing. That is a large payment
 * on the half of this tool that is plumbing.
 *
 * THE TABLE BELOW WAS FITTED FOR THIS BUILD, not inherited. Corpus: 2,170
 * hand-written files across eighteen repositories and six languages — Python,
 * Go, Rust, TypeScript, JavaScript and prose — every tree filtered through
 * `git ls-files` so ignored files never entered it, with minified and vendored
 * files excluded because nobody hand-bundles those. 27.4 MB of text, 8,184,534
 * real o200k tokens.
 *
 * Cross-validated over 500 simulated 200-file bundles, which is the only level
 * the number is ever shown at:
 *
 *     estimator             p5      p50      p95   worst
 *     ─────────────────────────────────────────────────────
 *     bytes ÷ 4          −25.7    −16.1     −8.2    29.3     ← undercounts
 *     one global divisor −11.3     +0.3     +9.7    15.5
 *     this table          −9.0     −1.4     +5.2    12.6
 *
 * BYTES ÷ 4 FAILS IN THE DANGEROUS DIRECTION. It says "97k, you fit in 128k" for
 * a bundle that is really 113k — silently overflowing the window this feature
 * exists to keep you inside.
 *
 * `.json` genuinely needs splitting, and by more than the gap between languages:
 * a lockfile is 2.60 bytes per token where authored JSON data is 3.18.
 */

export interface TokenEstimate {
  readonly tokens: number;
  /** The measured 90% band, so the interface can show a range rather than a point. */
  readonly low: number;
  readonly high: number;
}

/** Fitted 2026-08-15. See the header for the corpus and the cross-validation. */
export const GLOBAL_DIVISOR = 3.35;

const DIVISORS: Record<string, number> = {
  html: 3.65, htm: 3.65, astro: 3.67, vue: 3.65, svelte: 3.65,
  ts: 3.51, tsx: 3.51, mts: 3.51, cts: 3.51,
  js: 3.93, jsx: 3.93, mjs: 3.54, cjs: 3.54,
  css: 3.49, scss: 3.49, sass: 3.49, less: 3.49,
  py: 4.22, pyi: 4.22,
  rs: 4.08, go: 3.55,
  md: 3.85, markdown: 3.85, mdx: 4.23, rst: 4.31, txt: 4.83,
  csv: 2.03, tsv: 2.03,
  yaml: 2.54, yml: 3.71, toml: 3.38,
};

/** The measured p5/p95 band, applied to the total rather than per file. */
const BAND_LOW = 0.91;
const BAND_HIGH = 1.05;

function divisorFor(path: string): number {
  const base = path.slice(path.lastIndexOf('/') + 1).toLowerCase();
  const dot = base.lastIndexOf('.');
  const ext = dot > 0 ? base.slice(dot + 1) : '';

  if (ext === 'json' || ext === 'webmanifest') {
    if (/-lock\.json$|^package-lock\.json$/.test(base)) return 2.6;
    if (/^(?:package|tsconfig|jsconfig|manifest|angular|nest-cli|components)\.json$|^tsconfig\..*\.json$|\.webmanifest$/.test(base)) {
      return 3.05;
    }
    return 3.18;
  }
  return DIVISORS[ext] ?? GLOBAL_DIVISOR;
}

/** Tokens for one file. Byte length, not character length — the ratio is per byte. */
export function estimateFile(path: string, byteLength: number): number {
  return byteLength / divisorFor(path);
}

export function estimateBundle(files: readonly { path: string; bytes: number }[]): TokenEstimate {
  let tokens = 0;
  for (const f of files) tokens += estimateFile(f.path, f.bytes);
  return {
    tokens: Math.round(tokens),
    low: Math.round(tokens * BAND_LOW),
    high: Math.round(tokens * BAND_HIGH),
  };
}

/** UTF-8 byte length without allocating an encoder per call. */
const encoder = new TextEncoder();
export function byteLength(text: string): number {
  return encoder.encode(text).length;
}

export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
